import { Component, effect, input, InputSignal, signal } from '@angular/core';
import { CardStatus } from '../../../../../../interfaces';
import { Transport } from 'src/app/pages/custom-route/interfaces/custom-route.interface';
import { TranslateModule } from '@ngx-translate/core';
import { TransportListCardOverflowLineListComponent } from "./components/transport-list-card-overflow-line-list/transport-list-card-overflow-line-list.component";
import { TransportListCardStandardTransferComponent } from "./components/transport-list-card-standard-transfer/transport-list-card-standard-transfer.component";
import { TransportListCardIconListOverflowComponent } from "./components/transport-list-card-icon-list-overflow/transport-list-card-icon-list-overflow.component";

@Component({
  selector: 'app-transport-list-card',
  standalone: true,
  imports: [
    TranslateModule,
    TransportListCardOverflowLineListComponent,
    TransportListCardStandardTransferComponent,
    TransportListCardIconListOverflowComponent
],
  templateUrl: './transport-list-card.component.html',
  styleUrls: ['./transport-list-card.component.scss'],
})
export class TransportListCardComponent {
  public transportList: InputSignal<Transport[]> = input.required<Transport[]>();
  public maxHeight: InputSignal<number> = input.required<number>();
  public isOverflow: InputSignal<boolean> = input.required<boolean>();
  public pointTitle: InputSignal<string> = input.required<string>();
  public previousPointTitle: InputSignal<string> = input.required<string>();
  public status: InputSignal<CardStatus> = input.required<CardStatus>();  
  public indexCard: InputSignal<number> = input.required<number>();

  public marginBottomTransportList = signal([]);
  
  setMarginBottomTransportList = effect(() => {
    if (this.transportList().length <= 0 || !this.maxHeight()) return;
    
    const totalAvailableMarginBottom = this.maxHeight() + 12 < 48 ? 48 : this.maxHeight() + 12;
    const totalTransportListDuration = this.transportList().reduce((acc, transport) => acc + transport.duration, 0);
    
    // Reset marginBottomTransportList before filling it again
    this.marginBottomTransportList.set([]);
    
    if (this.isOverflow()) {
      this.transportList().forEach(transport => {
        const transportDuration = transport.duration;
        const marginBottom = ((totalAvailableMarginBottom - 12) * transportDuration) / totalTransportListDuration;
        this.marginBottomTransportList().push(marginBottom);
      });
      
    } else {
      // ! Every transport item should have at least 48px height
      // 1. Calculate initial proportional values
      const proportionalValues = this.transportList().map(transport => {
        return (totalAvailableMarginBottom * transport.duration) / totalTransportListDuration;
      });

      // 2. Identify which ones need the minimum of 48px
      const itemsNeedingMinimum = proportionalValues.filter(value => value < 48);

      // 3. Calculate space used by items with minimum of 48px
      const spaceUsedByMinimumItems = itemsNeedingMinimum.length * 48;

      // 4. Calculate remaining space to distribute proportionally
      const remainingSpace = totalAvailableMarginBottom - spaceUsedByMinimumItems;

      // 5. Calculate total duration of items that do NOT need minimum
      const durationOfItemsAboveMinimum = this.transportList()
        .map((transport, index) => ({ transport, proportional: proportionalValues[index] }))
        .filter(item => item.proportional >= 48)
        .reduce((acc, item) => acc + item.transport.duration, 0);
      
      // 6. Distribute remaining space
      const finalValues: number[] = [];
      
      this.transportList().forEach((transport, index) => {
        const proportionalValue = proportionalValues[index];
        
        if (proportionalValue < 48) {
          finalValues.push(48);
        } else {
          const adjustedMarginBottom = durationOfItemsAboveMinimum > 0 
            ? (remainingSpace * transport.duration) / durationOfItemsAboveMinimum
            : 48; // Fallback if no items above minimum

          finalValues.push(adjustedMarginBottom);
        }
      });
      
      this.marginBottomTransportList.set(finalValues);
      return;
    }
  }, { allowSignalWrites: true });
}
